<script setup lang="ts">
import { logoImages, vegetableListSvgImages } from '~/models/images'
import type { CMSMunicipalityPage, CMSStep } from '#shared/types/cmsLayout.ts'
import { useLivePreview } from '@payloadcms/live-preview-vue'
import type { BreadcrumbItem } from '@nuxt/ui'
import { heroImages } from '#shared/models/images.ts'
import type { UseSchemaOrgInput } from 'nuxt-schema-org/schema'

definePageMeta({
  layout: false
})

interface CMSDataResponse {
  page: CMSMunicipalityPage
  relatedPages: CMSBlog[]
}

const localeStore = useLocaleStore()
const locale = computed(() => localeStore.locale)

const route = useRoute('gemeente-slug')
const isPreview = computed(() => !!route.query.preview)

const { t } = useI18n()

const { data: amountOfAvailableCooks } = await useFetch<number>(
  '/api/fetch-available-cooks',
  {
    //FIXME: Temporary fix
    server: false,
    query: {
      municipality: route.params.slug
    }
  }
)

const { data: currentMunicipality, error: municipalityError } =
  await useAsyncData<CMSDataResponse>(
    `municipality/${route.params.slug}${isPreview.value ? `?preview=${route.query.preview}` : ''}`,
    () =>
      $fetch('/api/website-page/fetch-slug', {
        query: {
          slug: route.params.slug,
          type: 'municipality-pages',
          preview: route.query.preview ?? ''
        }
      })
  )

if (municipalityError.value) {
  throw createError({
    status: municipalityError.value.status,
    statusText: municipalityError.value.statusText
  })
}

const config = useRuntimeConfig()
let pageData: Ref<CMSMunicipalityPage> | undefined
if (isPreview.value && currentMunicipality.value) {
  const { data: tempData } = useLivePreview<CMSMunicipalityPage>({
    initialData: currentMunicipality.value.page,
    serverURL: config.public.cmsUrl
  })
  pageData = tempData
} else if (currentMunicipality.value) {
  pageData = toRef(currentMunicipality.value.page)
}

const initialData = computed<CMSDataResponse | undefined>(() => {
  if (!pageData?.value) {
    return undefined
  }

  return {
    page: pageData.value,
    relatedPages: currentMunicipality.value?.relatedPages ?? []
  }
})

const computedHero = computed(() => {
  if (!initialData.value) {
    return undefined
  }

  return {
    ...initialData.value.page.hero,
    image: heroImages[initialData.value.page.hero.image]
  }
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  if (!initialData.value) {
    return []
  }

  return [
    {
      to: route.path,
      label: initialData.value.page.hero.title[locale.value]
    }
  ]
})

watchEffect(() => {
  if (!initialData.value) {
    return
  }

  const currentData = initialData.value.page
  const thuisgekooktName = t('links.thuisgekookt')
  const title = currentData.hero.title[locale.value]

  const baseSchemas: UseSchemaOrgInput = [
    defineOrganization({
      name: thuisgekooktName,
      logo: logoImages.thuisgekookt.src,
      sameAs: [
        'https://www.facebook.com/ThuisgekooktNL/',
        'https://www.instagram.com/thuisgekookt/',
        'https://x.com/thuisgekookt',
        'https://nl.pinterest.com/infothuisgekookt/',
        'https://www.linkedin.com/company/thuisgekookt',
        'https://www.youtube.com/channel/UCYzMazH-n0hx2GObF5jD2VQ'
      ]
    }),

    defineWebPage({
      '@type': 'WebPage',
      name: title,
      url: `https://thuisgekookt.nl${route.path}`
    }),

    defineBreadcrumb({
      itemListElement: [{ name: thuisgekooktName, item: '/' }, { name: title }]
    })
  ]

  const stepSchemas = currentData.sections
    .filter((section): section is CMSStep => section.blockType === 'steps')
    .map((step) =>
      defineHowTo({
        name: step.title[locale.value],
        step: step.items.map((item, index) => ({
          url: currentData.slug
            ? `https://thuisgekookt.nl/gemeente/${currentData.slug}#step-${index + 1}`
            : 'https://thuisgekookt.nl/',
          name: item.title[locale.value],
          text: item.content[locale.value],
          image: vegetableListSvgImages[index]
        }))
      })
    )

  useSchemaOrg(baseSchemas.concat(stepSchemas))
})
</script>

<template>
  <div>
    <CMSStaticLayout
      v-if="initialData"
      type="focus-municipality-pages"
      :data="initialData.page"
      :hero="computedHero"
      :breadcrumbs
      :placeholder="{ amountOfAvailableCooks }"
    />
    <CMSPreviewWidget v-if="isPreview" />
  </div>
</template>

<style scoped></style>
